import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import { LottieRefCurrentProps } from 'lottie-react';
import { forwardRef } from 'react';
import { getRootUrl } from '@share-a-cart/shared-utils';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  CartTitle,
  ContinueTotalBlock,
  TooltipStyled,
  VendorListAndPriceDisc,
  VendorLogoBlock,
} from 'src/components';
import { useCopyToClipboard } from 'src/hooks';
import type { CartApprovalStatus } from 'src/types';
import { GetReviewHeaderAllCartsBtn } from './GetReviewHeaderAllCartsBtn';
// import { CartTotalQty } from '../../cartHistory/cartDisplayBlocks/reusables';

type Props = {
  handleContinue?: (skipApproval: boolean) => void;
  handleApproveOrReject?: (response: 'approve' | 'reject') => void;
  cartId: string;
  cartTotalQty: number;
  pricesAsOf: number;
  cartTitle: string | undefined;
  cartPrice: string;
  cartVendor: string;
  cartVendorDisplayName: string;
  cartCreated: boolean;
  cartAp?: string;
  cartLocale?: string;
  displaySourceSite: string | undefined;
  isLoading: boolean;
  cartStore?: string;
  page?: 'get' | 'embed' | 'review';
  requestedByEmail: string;
  orgApprovalStatus?: CartApprovalStatus;
  whitePendingStatus?: boolean;
};

export const GetReviewHeader = forwardRef<LottieRefCurrentProps, Props>(
  (
    {
      handleContinue,
      handleApproveOrReject,
      cartId,
      cartTotalQty,
      pricesAsOf,
      cartTitle,
      cartPrice,
      cartVendor,
      cartVendorDisplayName,
      cartCreated,
      cartAp,
      cartLocale,
      cartStore,
      displaySourceSite,
      isLoading,
      page = 'get',
      requestedByEmail,
      orgApprovalStatus,
      whitePendingStatus,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const copyToClipboard = useCopyToClipboard();

    return (
      <div
        className={`bg-primary-dark pb-8 ${page === 'review' ? 'pt-10 sm:pt-12 md:pt-14 lg:pt-16' : 'pt-0'}`}
      >
        <div className='mx-auto flex w-full max-w-[1440px] flex-row flex-wrap items-end justify-between gap-y-8 px-6 smMed:gap-x-4 md:px-10 lg:px-12'>
          <div className='flex w-full flex-col items-start justify-center gap-4 sm:w-auto'>
            <GetReviewHeaderAllCartsBtn page={page} />
            {isLoading ? (
              <div className='skeleton-dark h-[31px] w-[260px] md:h-[39px]'></div>
            ) : cartTitle ? (
              <CartTitle
                cartId={cartId}
                cartTitle={cartTitle}
                cartCreated={cartCreated}
                forGetPage={true}
                cartVendorDisplayName={cartVendorDisplayName}
                linkToReviewPage={false}
              />
            ) : null}

            <div className='flex w-full flex-row flex-wrap items-center justify-start gap-2 smMed:w-auto'>
              <div className='inline-flex w-fit items-center justify-center'>
                {isLoading ? (
                  <div className='skeleton-dark h-8 w-8'></div>
                ) : (
                  <VendorLogoBlock vendor={cartVendor} size='medium' />
                )}
                {isLoading ? (
                  <div className='skeleton-dark mx-2 h-8 w-[60px]'></div>
                ) : (
                  <span className='heading-200 px-2 py-[2.5px] text-offWhite'>{cartId}</span>
                )}
                {isLoading ? (
                  <div className='skeleton-dark mr-[1px] h-8 w-[19px]'></div>
                ) : (
                  <TooltipStyled title={t('common.copy_link_to_list')}>
                    <div
                      onClick={() => copyToClipboard(`${getRootUrl()}/get/${cartId}`)}
                      className='flex cursor-pointer items-center justify-center fill-offGray hover:fill-gray-300'
                    >
                      <ContentCopyIcon
                        className='transition-all'
                        style={{ fontSize: 20, fill: 'inherit' }}
                      />
                    </div>
                  </TooltipStyled>
                )}
                {isLoading ? (
                  <div className='skeleton-dark ml-[1px] h-8 w-[19px]'></div>
                ) : (
                  <TooltipStyled title={t('common.share_list')}>
                    <Link
                      to={`/share/${cartId}`}
                      reloadDocument
                      className='flex cursor-pointer items-center justify-center fill-offGray hover:fill-gray-300'
                    >
                      <ShareOutlinedIcon
                        className='transition-all'
                        style={{ fontSize: 20, fill: 'inherit' }}
                      />
                    </Link>
                  </TooltipStyled>
                )}
              </div>
              {isLoading ? (
                <div className='skeleton-dark h-[22px] w-[240px]'></div>
              ) : (
                <VendorListAndPriceDisc
                  timestamp={pricesAsOf}
                  itemCount={cartTotalQty}
                  vendorDisplayName={cartVendorDisplayName || undefined}
                  displaySourceSite={displaySourceSite}
                  includeMarginRight={true}
                  cartStore={cartStore}
                  cartVendor={cartVendor}
                />
              )}
            </div>
          </div>
          <ContinueTotalBlock
            ref={ref}
            handleContinue={handleContinue}
            handleApproveOrReject={handleApproveOrReject}
            totalPrice={cartPrice}
            totalQty={cartTotalQty}
            forGetHeader={true}
            forReviewFooter={false}
            isLoading={isLoading}
            page={page}
            requestedByEmail={requestedByEmail}
            orgApprovalStatus={orgApprovalStatus}
            cartId={cartId}
            whitePendingStatus={whitePendingStatus}
            cartVendor={cartVendor}
            cartAp={cartAp}
            cartLocale={cartLocale}
          />
        </div>
      </div>
    );
  },
);
